import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { Async, type AsyncState } from '../../platform/async/Async'
import { Skeleton } from '../../platform/async/Skeleton'
import { Button } from '../../platform/primitives/Button'
import { Panel } from '../../platform/primitives/Panel'
import { useBreakpoint } from '../../platform/responsive/useBreakpoint'
import { useAccounts } from '../accounts/useAccounts'
import { useCategories } from '../categories/useCategories'
import { RuleFlow, type RuleFlowHandle } from '../rules/RuleFlow'
import { dayGroups } from './dayGroups'
import { activeFilterCount, filterIdentity, readFilters, writeFilters, type TxnFilters } from './filters'
import { BulkDialog, type BulkRequest } from './BulkDialogs'
import { DeleteTransactionsDialog } from './DeleteTransactionsDialog'
import { BulkToolbar, type BulkToolbarAction } from './BulkToolbar'
import { ruleSeed } from './ruleSeed'
import { emit } from '../../platform/analytics/analytics'
import { emitFiltersApplied, useListAnalytics } from './transactionsAnalytics'
import { SelectionProvider, usePageSelection, useSelection } from './selection'
import { FilterBar } from './FilterBar'
import { ReviewChip, type ReviewChipSlotProps } from './filterControls'
import { ReviewCard } from '../review/ReviewCard'
import { ExportButton } from './ExportButton'
import { Pager, rangeOf } from './Pager'
import { TransactionCards } from './TransactionCards'
import { TransactionGrid } from './TransactionGrid'
import { useCategoryQuickPick } from './useCategoryQuickPick'
import { useMerchantQuickPick } from './useMerchantQuickPick'
import type { Txn } from './transactionWire'
import { isPastMonth, monthName } from './txnDisplay'
import { useBulkActions } from './useBulkActions'
import { useFilterOptions } from './useFilterOptions'
import { usePreviousPageTail, useReviewCount, useTransactions, type TxnPage } from './useTransactions'
import './transactions.css'

/** The default month is "this month", and it must not change under a mounted screen. */
function useToday(): Date {
  const [today] = useState(() => new Date())
  return today
}

/**
 * Is the zero-row result the user's own doing? A search or a filter can empty
 * the list; that is not "no transactions yet", and gets a different message and
 * a way back. A month alone is not a narrowing: an empty month is its own case.
 */
function isNarrowed(f: TxnFilters): boolean {
  return activeFilterCount(f) > 0 || f.q.trim() !== ''
}

/**
 * The selection is tied to the filters (minus the page), so the provider sits
 * above the screen and reads the same URL the screen does.
 */
export function Transactions() {
  const [params] = useSearchParams()
  const today = useToday()
  const filters = useMemo(() => readFilters(params, today), [params, today])
  return (
    <SelectionProvider identity={filterIdentity(filters)} onStarted={() => emit('bulk_selection_started')}>
      <TransactionsScreen filters={filters} today={today} />
    </SelectionProvider>
  )
}

function TransactionsScreen({ filters, today }: { filters: TxnFilters; today: Date }) {
  const [, setParams] = useSearchParams()
  const navigate = useNavigate()
  const phone = useBreakpoint() === 'phone'
  const { dispatch, selected, generation } = useSelection()
  const bulkActions = useBulkActions({ onPerformed: (e) => emit('bulk_action_performed', e) })
  /** The open bulk dialog and the filter generation it opened under. */
  const [bulkOpen, setBulkOpen] = useState<{ request: BulkRequest; generation: number } | null>(null)
  const bulk = bulkOpen?.request ?? null
  const setBulk = (request: BulkRequest | null) => setBulkOpen(request === null ? null : { request, generation })
  const bulkRestore = useRef<HTMLElement | null>(null)
  /** Ids frozen when Delete was pressed; the confirm dialog is open while this is set. */
  const [deleting, setDeleting] = useState<string[] | null>(null)
  const ruleFlow = useRef<RuleFlowHandle>(null)
  /** Phone select mode: cards show boxes and a tap toggles. Done leaves it; the selection stays. */
  const [selecting, setSelecting] = useState(false)
  const pick = useCategoryQuickPick()
  const merchantPick = useMerchantQuickPick()
  /** In select mode the checkbox is the row's control, so the pills stay plain text (the merchant one is not drawn). */
  const onCategoryClick = selecting ? undefined : pick.onCategoryClick
  const onMerchantClick = selecting ? undefined : merchantPick.onMerchantClick
  const { fetching, ...list } = useTransactions(filters)
  const reviewCount = useReviewCount(filters)
  useListAnalytics(list, filters, reviewCount)
  const filterOptions = useFilterOptions()
  const accountsState = useAccounts()
  const categoriesState = useCategories()

  const accounts = useMemo(
    () =>
      new Map(
        accountsState.status === 'success' || accountsState.status === 'stale'
          ? accountsState.data.map((a) => [a.id, a] as const)
          : [],
      ),
    [accountsState],
  )
  const categories = useMemo(
    () =>
      new Map(
        categoriesState.status === 'success' ? categoriesState.data.map((c) => [c.id, c.name] as const) : [],
      ),
    [categoriesState],
  )

  const rows = list.status === 'success' || list.status === 'stale' ? list.data.rows : NO_ROWS
  // "(continued)" belongs to the rows ON SCREEN. During a page turn the previous
  // page is still shown while the URL already names the next, so the page whose
  // predecessor matters is the one the data says it is, not the URL's.
  const shownPage = list.status === 'success' || list.status === 'stale' ? list.data.page : filters.page
  const continuesFrom = usePreviousPageTail({ ...filters, page: shownPage })
  const selection = usePageSelection(rows)
  // Every refetch refreshes the snapshots of the selected rows now on screen,
  // so the selection count and the bulk dialogs describe what the server
  // holds. It never adds a row. "Add tag" does not depend on it: that action
  // re-reads each row before it PATCHes.
  useEffect(() => {
    if (rows.length > 0) dispatch({ t: 'replace', rows: [...rows] })
  }, [rows, dispatch])

  const setPage = (page: number) => setParams(writeFilters({ ...filters, page }, today))
  const clearFilters = () => setParams(writeFilters(readFilters(new URLSearchParams(), today), today))

  const gridWrap = useRef<HTMLDivElement>(null)
  const onBulkAction = (action: BulkToolbarAction) => {
    if (action === 'delete') {
      setDeleting(selected.map((r) => r.id))
      return
    }
    if (action !== 'create_rule') {
      setBulk({ kind: action, rows: selected })
      return
    }
    // Create rule opens M11's builder, not a dialog of this screen's own (Q18).
    const draft = ruleSeed(selected)
    if (draft !== null) ruleFlow.current?.openBuilder(draft)
  }
  /** After a run the toolbar's own button may be gone (everything succeeded and the selection emptied),
   *  and with it the opener: focus goes back to the grid's one tab stop instead of to <body>. */
  const closeBulk = () => {
    bulkRestore.current = gridWrap.current?.querySelector<HTMLElement>('[data-cell][tabindex="0"], [data-cell] [tabindex="0"]') ?? null
    setBulk(null)
  }
  /** Paging moves focus to the grid. The cell is focused now, while the previous
   *  page is still on screen (`keepPreviousData`): the grid restores focus to
   *  the same position when the new rows replace it, so it never falls to body. */
  const pageTo = (page: number) => {
    gridWrap.current?.querySelector<HTMLElement>('[data-cell][tabindex="0"], [data-cell] [tabindex="0"]')?.focus()
    setPage(page)
  }

  // Whether zero rows means "nothing yet" or "nothing matches" is the screen's
  // call, so the hook reports success and the empty status is derived here.
  const narrowed = isNarrowed(filters)
  const state: AsyncState<TxnPage> =
    list.status === 'success' && list.data.total === 0 && !narrowed ? { status: 'empty' } : list
  // Only a household with no accounts is told to connect one. Anyone else with
  // zero rows has an empty month (or, all time, nothing synced yet).
  // `useAccounts` reports a zero-length list as `empty` (it carries no data), never as success or stale
  // with `[]`, so `empty` is the only spelling of "no accounts".
  const noAccounts = accountsState.status === 'empty'
  const showAllTime = () => setParams(writeFilters({ ...filters, month: null, page: 1 }, today))
  const empty = noAccounts
    ? {
        title: 'No transactions yet',
        body: 'Connect an account and your transactions will appear here.',
        action: { label: 'Connect an account', onAct: () => navigate('/accounts/connect') },
      }
    : filters.month !== null
      ? {
          // A closed month will not fill up, so only the current one says "yet".
          title: isPastMonth(filters.month, today)
            ? `No transactions in ${monthName(filters.month, today)}`
            : `No transactions in ${monthName(filters.month, today)} yet`,
          body: isPastMonth(filters.month, today)
            ? 'Nothing was recorded in this month.'
            : 'Transactions appear here as your accounts sync.',
          action: { label: 'Show all time', onAct: showAllTime },
        }
      : {
          title: 'No transactions yet',
          body: 'Transactions appear here as your accounts sync.',
          action: { label: 'View accounts', onAct: () => navigate('/accounts') },
        }

  const total = list.status === 'success' ? list.data.total : undefined
  const subtitle =
    total === undefined
      ? null
      : `${total} in ${monthName(filters.month, today)}${reviewCount !== undefined && reviewCount > 0 ? ` · ${reviewCount} to review` : ''}`

  return (
    <div className="txn">
      <header className="txn-header">
        <div className="txn-header__title">
          <h2>Transactions</h2>
          {subtitle !== null && <p className="txn-header__sub">{subtitle}</p>}
        </div>
        {/* No Import (Q10) and no Add transaction: quick entry is out of scope. */}
        <ExportButton month={filters.month} today={today} />
        {phone && (
          <Button
            type="button"
            size="page"
            variant="outline"
            aria-pressed={selecting}
            onClick={() => setSelecting((on) => !on)}
          >
            {selecting ? 'Done' : 'Select'}
          </Button>
        )}
      </header>

      <ReviewCard />

      <FilterBar
        filters={filters}
        options={filterOptions}
        reviewCount={reviewCount}
        ReviewChipFor={ConnectedReviewChip}
        today={today}
        onChange={(next) => {
          emitFiltersApplied(filters, next)
          setParams(writeFilters(next, today))
        }}
      />

      {!phone && <BulkToolbar onAction={onBulkAction} />}
      <RuleFlow ref={ruleFlow} />
      <BulkDialog
        request={bulk}
        stale={bulkOpen !== null && bulkOpen.generation !== generation}
        options={filterOptions}
        run={bulkActions.run}
        progress={bulkActions.progress}
        onClose={closeBulk}
        restoreFocusTo={bulkRestore}
      />

      <Panel className={`txn-panel${phone ? ' txn-panel--flow' : ''}`}>
        <Async
          surface="transactions"
          state={state}
          skeleton={
            <div className="txn-skeleton">
              <Skeleton height={44} />
              <Skeleton height={44} />
              <Skeleton height={44} />
              <Skeleton height={44} />
              <Skeleton height={44} />
              <Skeleton height={44} />
            </div>
          }
          empty={empty}
          announce={(p) => `Showing ${rangeOf(p).from} to ${rangeOf(p).to} of ${p.total}`}
        >
          {(page) => (
            <>
              {phone && page.rows.length > 0 ? (
                // M6-R24: below 640px a six-column table does not fit, so the
                // list is a stack of cards. A zero-row page (filtered empty)
                // keeps the grid, whose empty state carries the headers and
                // Clear filters.
                <TransactionCards
                  groups={dayGroups(page.rows, { continuesFrom })}
                  accounts={accounts}
                  categories={categories}
                  categoriesStatus={categoriesState.status}
                  selecting={selecting}
                  selection={selection}
                  onCategoryClick={onCategoryClick}
                  onMerchantClick={onMerchantClick}
                  onLongPress={(row) => {
                    setSelecting(true)
                    dispatch({ t: 'toggle', row })
                  }}
                />
              ) : (
                <div ref={gridWrap} className="txn-panel__grid">
                  <TransactionGrid
                    groups={dayGroups(page.rows, { continuesFrom })}
                    accounts={accounts}
                    categories={categories}
                    categoriesStatus={categoriesState.status}
                    selection={selection}
                    onCategoryClick={onCategoryClick}
                    onMerchantClick={onMerchantClick}
                    empty={
                      <div className="txn-empty">
                        <p>No transactions match these filters</p>
                        <Button size="panel" onClick={clearFilters}>
                          Clear filters
                        </Button>
                      </div>
                    }
                  />
                </div>
              )}
              <Pager page={page} fetching={fetching} onPage={pageTo} />
            </>
          )}
        </Async>
      </Panel>
      {/* Phone: after the panel, so the page's height includes the bar (see .txn-bulk--docked). */}
      {phone && <BulkToolbar docked onAction={onBulkAction} />}
      {deleting !== null && (
        <DeleteTransactionsDialog
          ids={deleting}
          restoreFocusTo={bulkRestore}
          onDeleted={() => dispatch({ t: 'clear' })}
          onClose={() => {
            bulkRestore.current = gridWrap.current?.querySelector<HTMLElement>('[data-cell][tabindex="0"], [data-cell] [tabindex="0"]') ?? null
            setDeleting(null)
          }}
        />
      )}
      {pick.dialog}
      {merchantPick.dialog}
    </div>
  )
}

const NO_ROWS: Txn[] = []

/** The sheet's chip for the month it is drafting. A component, so its query hook always runs. */
function ConnectedReviewChip({ month, pressed, onToggle }: ReviewChipSlotProps) {
  return <ReviewChip pressed={pressed} count={useReviewCount({ month })} onToggle={onToggle} />
}
